import React, { Component } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core'
import { faPlus, faMinus, faArrowUp, faArrowDown, faTrashAlt, faTimes, faCheck, faExclamation, faInfoCircle } from '@fortawesome/free-solid-svg-icons'

import { ToastContainer, toast, Slide } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';

import YubinukiChart from './components/YubinukiChart';
import { Yubinuki } from './components/Yubinuki';
import ChartEngine from './components/ChartEngine';
import DefaultFactory from './components/DefaultFactory';
import YubinukiBasicSettings from './components/YubinukiBasicSettings';
import YubinukiProcedureGroupList from './components/YubinukiProcedureGroupList';
import YubinukiStorageControl from './components/YubinukiStorageControl';
import StorageControl from './components/StorageControl';
import './App.scss';

// initialize FontAwesome
library.add(faPlus, faMinus, faArrowUp, faArrowDown, faTrashAlt, faTimes, faCheck, faExclamation, faInfoCircle);
// initialize end

function notifySuccess(message: string): React.ReactText {
  return toast(<div className="messageRow"><div className="icon"><FontAwesomeIcon icon="check" size="lg" color="green" /></div><div className="message">{message}</div></div>, {
    progressClassName: "toastProgressBar--success",
  });
}

function notifyWarn(message: string): React.ReactText {
  return toast(<div className="messageRow"><div className="icon"><FontAwesomeIcon icon="exclamation" size="lg" color="#f9a11b" /></div><div className="message">{message}</div></div>, {
    progressClassName: "toastProgressBar--warn",
    position: "top-center",
    autoClose: 7000,
  });
}

function addAndDeleteLimitationAlert(title: string, message: string, onClose: (() => void)) {
  return (
    <div className="react-confirm-alert customAlert">
        <div className="alertBody">
            <h1>{title}</h1>
            {message}
            <div className="buttonGroup">
                <button className="btn btn-outline-secondary" onClick={onClose}>わかりました</button>
            </div>
        </div>
    </div>
  )
}

function moveOverGroupAlert(message: string, detailMessage: string, onClose: (() => void), onClick: (() => void)) {
  return (
    <div className="react-confirm-alert customAlert">
        <div className="alertBody">
            <h1>確認</h1>
            {message}<br/>
            {detailMessage}
            <div className="buttonGroup">
                <button className="btn mr-2" onClick={onClose}>キャンセル</button>
                <button className="btn btn-secondary" onClick={() => { onClick(); onClose(); }}>移動します</button>
            </div>
        </div>
    </div>
  )
}

const AppVersion = "1.2.0";

const LengthLimitation = {
  procedureGroup: 16,
  procedure: 16,
  ito: 50,
};

interface Props {
  yubinuki: Yubinuki,
};

interface State {
  yubinuki: Yubinuki,
  slotKey: string | undefined,
  procedureGroupsLength: number,
}

class App extends Component<Props, State> {
  toastId: string | number | undefined = undefined;

  constructor(props: Props) {
    super(props);
    this.state = {
      yubinuki: props.yubinuki,
      slotKey: undefined,
      // starts with zero and set last empty one
      procedureGroupsLength: props.yubinuki.procedures.map((proc)=>proc.groupNo).reduce((a,b)=>Math.max(a,b)) + 2,
    };

    this.clearToastIfNeeded = this.clearToastIfNeeded.bind(this);
    this.handleLoadYubinuki = this.handleLoadYubinuki.bind(this);
    this.handleSaveYubinuki = this.handleSaveYubinuki.bind(this);

    this.handleChangeKoma = this.handleChangeKoma.bind(this);
    this.handleChangeTobi = this.handleChangeTobi.bind(this);
    this.handleChangeResolutionAuto = this.handleChangeResolutionAuto.bind(this);
    this.handleChangeResolution = this.handleChangeResolution.bind(this);

    this.handleAddProcedureGroup = this.handleAddProcedureGroup.bind(this);

    this.handleChangeProcedureOffset = this.handleChangeProcedureOffset.bind(this);
    this.handleChangeProcedureSasiType = this.handleChangeProcedureSasiType.bind(this);
    this.handleChangeProcedureMatomeSasi = this.handleChangeProcedureMatomeSasi.bind(this);
    this.handleChangeProcedureConnectedRounds = this.handleChangeProcedureConnectedRounds.bind(this);

    this.handleMoveUpProcedure = this.handleMoveUpProcedure.bind(this);
    this.handleMoveUpProcedureOverGroups = this.handleMoveUpProcedureOverGroups.bind(this);
    this.handleMoveDownProcedure = this.handleMoveDownProcedure.bind(this);
    this.handleMoveDownProcedureOverGroups = this.handleMoveDownProcedureOverGroups.bind(this);
    this.handleDeleteProcedure = this.handleDeleteProcedure.bind(this);
    this.handleAddProcedure = this.handleAddProcedure.bind(this);

    this.handleChangeProcedureItoColor = this.handleChangeProcedureItoColor.bind(this);
    this.handleChangeProcedureItoRounds = this.handleChangeProcedureItoRounds.bind(this);
    this.handleMoveUpItoInProcedure = this.handleMoveUpItoInProcedure.bind(this);
    this.handleMoveDownItoInProcedure = this.handleMoveDownItoInProcedure.bind(this);
    this.handleDeleteItoInProcedure = this.handleDeleteItoInProcedure.bind(this);
    this.handleAddItoInProcedure = this.handleAddItoInProcedure.bind(this);
  }

  clearToastIfNeeded() {
    if (this.toastId) {
      toast.dismiss(this.toastId);
    }
  }

  handleLoadYubinuki(slotKey: string) {
    let data = StorageControl.load(slotKey);
    if (data === undefined) {
      this.clearToastIfNeeded();
      this.toastId = notifyWarn(`読みこめませんでした。スロット '${slotKey}' にデータはありません。`);
    } else {
      this.setState({ yubinuki: data.yubinuki, slotKey: slotKey });
      this.clearToastIfNeeded();
      this.toastId = notifySuccess(`'${slotKey}' から読みこみました。`);
    }
  }

  handleSaveYubinuki(slotKey: string, title: string) {
    let data = { yubinuki: this.state.yubinuki, title: title };
    StorageControl.save(slotKey, data);
    this.setState({ slotKey: slotKey });
    this.clearToastIfNeeded();
    this.toastId = notifySuccess("保存しました");
  }

  handleChangeKoma(koma: number) {
    let prevState = this.state;
    prevState.yubinuki.koma = koma;
    this.setState(prevState);
  }

  handleChangeTobi(tobi: number) {
    let prevState = this.state;
    prevState.yubinuki.tobi = tobi;
    this.setState(prevState);
  }

  handleChangeResolution(resolution: number) {
    let prevState = this.state;
    prevState.yubinuki.resolution = resolution;
    this.setState(prevState);
  }

  handleChangeResolutionAuto(resolutionAuto: boolean) {
    let prevState = this.state;
    prevState.yubinuki.resolutionAuto = resolutionAuto;
    this.setState(prevState);
  }

  handleAddProcedureGroup() {
    let prevState = this.state;
    if (prevState.procedureGroupsLength >= LengthLimitation.procedureGroup) {
      confirmAlert({
        customUI: ({ onClose }) => { return addAndDeleteLimitationAlert("グループを追加できません。", `グループをこれ以上追加できません。(最大 ${LengthLimitation.procedureGroup})`, onClose) }
      });
    } else {
      this.setState({ procedureGroupsLength: prevState.procedureGroupsLength + 1 });
    }
  }

  handleChangeProcedureOffset(procedureIndex: number, offset: number) {
    let prevState = this.state;
    prevState.yubinuki.procedures[procedureIndex].offset = offset;
    this.setState(prevState);
  }
  
  handleChangeProcedureSasiType(procedureIndex: number, sasiType: number) {
    let prevState = this.state;
    prevState.yubinuki.procedures[procedureIndex].sasiType = sasiType;
    this.setState(prevState);
  }

  handleChangeProcedureMatomeSasi(procedureIndex: number, matomeSasi: boolean) {
    let prevState = this.state;
    prevState.yubinuki.procedures[procedureIndex].matomeSasi = matomeSasi;
    this.setState(prevState);
  }

  handleChangeProcedureConnectedRounds(procedureIndex: number, connectedRounds: number) {
    let prevState = this.state;
    prevState.yubinuki.procedures[procedureIndex].connectedRounds = connectedRounds;
    this.setState(prevState);
  }

  handleMoveUpProcedure(procedureIndex: number) {
    if (procedureIndex > 0) {
      let yubinuki = this.state.yubinuki;
      let procA = yubinuki.procedures[procedureIndex];
      let procB = yubinuki.procedures[procedureIndex - 1];

      if (procA.groupNo !== procB.groupNo) {
        confirmAlert({
          customUI: ({ onClose }) => moveOverGroupAlert("上のグループへ移動しますか?", `(グループ${procA.groupNo + 1} → グループ${procA.groupNo})`, onClose, () => this.handleMoveUpProcedureOverGroups(procedureIndex) ),
        });
      } else {
        yubinuki.procedures[procedureIndex] = procB;
        yubinuki.procedures[procedureIndex - 1] = procA;
        this.setState({ yubinuki: yubinuki });
      }
    }
  }

  handleMoveUpProcedureOverGroups(procedureIndex: number) {
    if (procedureIndex > 0) {
      let yubinuki = this.state.yubinuki;
      let procA = yubinuki.procedures[procedureIndex];
      let procB = yubinuki.procedures[procedureIndex - 1];
      procA.groupNo = procB.groupNo;
      this.setState({ yubinuki: yubinuki });
    }
  }

  handleMoveDownProcedure(procedureIndex: number) {
    let yubinuki = this.state.yubinuki;
    let groupsLength = this.state.procedureGroupsLength;
    let procA = yubinuki.procedures[procedureIndex];
    let nextItemExist = procedureIndex < yubinuki.procedures.length - 1;
    let nextGroupExist = procA.groupNo < groupsLength - 1;
    let moveOverGroup = function() {
      if (nextItemExist) {
        let procB = yubinuki.procedures[procedureIndex + 1];
        return procA.groupNo !== procB.groupNo;
      } else if (nextGroupExist) {
        return true;
      } else {
        return false;
      }
    }();

    if (moveOverGroup) {
      confirmAlert({
        customUI: ({ onClose }) => moveOverGroupAlert("下のグループへ移動しますか?", `(グループ${procA.groupNo + 1} → グループ${procA.groupNo + 2})`, onClose, () => this.handleMoveDownProcedureOverGroups(procedureIndex) ),
      });
    } else if (nextItemExist) {
      let procB = yubinuki.procedures[procedureIndex + 1];
      yubinuki.procedures[procedureIndex] = procB;
      yubinuki.procedures[procedureIndex + 1] = procA;
      this.setState({ yubinuki: yubinuki });
    }
  }

  handleMoveDownProcedureOverGroups(procedureIndex: number) {
    let yubinuki = this.state.yubinuki;
    let procA = yubinuki.procedures[procedureIndex];
    if (procedureIndex < yubinuki.procedures.length - 1) {
      let procB = yubinuki.procedures[procedureIndex + 1];
      procA.groupNo = procB.groupNo;
    } else {
      procA.groupNo += 1;
    }
    this.setState({ yubinuki: yubinuki });
  }

  handleDeleteProcedure(procedureIndex: number) {
    let prevState = this.state;
    if (prevState.yubinuki.procedures.length <= 1) {
      confirmAlert({
        customUI: ({ onClose }) => { return addAndDeleteLimitationAlert("手順を削除できません。", "手順をこれ以上削除して、0個にすることはできません。", onClose) }
      });
    } else {
      prevState.yubinuki.procedures.splice(procedureIndex, 1);
      this.setState(prevState);
    }
  }

  handleAddProcedure(groupNo: number) {
    let yubinuki = this.state.yubinuki;
    if (yubinuki.procedures.length >= LengthLimitation.procedure) {
      confirmAlert({
        customUI: ({ onClose }) => { return addAndDeleteLimitationAlert("手順を追加できません。", `手順をこれ以上追加することはできません。(最大 ${LengthLimitation.procedure})`, onClose)}
      });
    } else {
      let nextGroupIndex = yubinuki.procedures.findIndex((proc) => proc.groupNo === groupNo + 1);
      let insertPosition = (nextGroupIndex < 0) ? yubinuki.procedures.length : nextGroupIndex;
      yubinuki.procedures.splice(insertPosition, 0, DefaultFactory.defaultProcedure(groupNo))
      this.setState({ yubinuki: yubinuki });
    }
  }

  handleChangeProcedureItoColor(procedureIndex: number, itoIndex: number, color: string) {
    let prevState = this.state;
    prevState.yubinuki.procedures[procedureIndex].itos[itoIndex].color = color;
    this.setState(prevState);
  }

  handleChangeProcedureItoRounds(procedureIndex: number, itoIndex: number, rounds: number) {
    let prevState = this.state;
    prevState.yubinuki.procedures[procedureIndex].itos[itoIndex].rounds = rounds;
    this.setState(prevState);
  }

  handleMoveUpItoInProcedure(procedureIndex: number, itoIndex: number) {
    if (itoIndex > 0) {
      let prevState = this.state;
      let itos = prevState.yubinuki.procedures[procedureIndex].itos;
      let itoA = itos[itoIndex];
      let itoB = itos[itoIndex - 1];
      itos[itoIndex] = itoB;
      itos[itoIndex - 1] = itoA;
      this.setState(prevState);
    }
  }

  handleMoveDownItoInProcedure(procedureIndex: number, itoIndex: number) {
    let prevState = this.state;
    let procedure = prevState.yubinuki.procedures[procedureIndex];
    if (itoIndex < procedure.itos.length - 1) {
      let itoA = procedure.itos[itoIndex];
      let itoB = procedure.itos[itoIndex + 1];
      procedure.itos[itoIndex] = itoB;
      procedure.itos[itoIndex + 1] = itoA;
      this.setState(prevState);
    }
  }

  handleDeleteItoInProcedure(procedureIndex: number, itoIndex: number) {
    let prevState = this.state;
    if (prevState.yubinuki.procedures[procedureIndex].itos.length <= 1) {
      confirmAlert({
        customUI: ({ onClose }) => { return addAndDeleteLimitationAlert("糸を削除できません。", "糸をこれ以上削除して、0個にすることはできません。", onClose) }
      })
    } else {
      prevState.yubinuki.procedures[procedureIndex].itos.splice(itoIndex, 1);
      this.setState(prevState);
    }
  }

  handleAddItoInProcedure(procedureIndex: number) {
    let prevState = this.state;
    if (prevState.yubinuki.procedures[procedureIndex].itos.length >= LengthLimitation.ito) {
      confirmAlert({
        customUI: ({ onClose }) => { return addAndDeleteLimitationAlert("糸を追加できません。", `糸をこれ以上追加できません。(最大 ${LengthLimitation.ito})`, onClose) }
      });
    } else {
      prevState.yubinuki.procedures[procedureIndex].itos.push(DefaultFactory.defaultIto());
      this.setState(prevState);
    }
  }

  render() {
    let yubinuki = ChartEngine.adjustResolution(this.state.yubinuki);
    let step = ChartEngine.maxProcedureCount(yubinuki.koma, yubinuki.tobi) * yubinuki.resolution;

    return (
      <div className="YubinukiApp">
        <h1>加賀ゆびぬき刺し模様シミュレータ <span style={{ fontSize: "smaller" }}>v{AppVersion}</span></h1>
        <div className="alert alert-primary d-block d-md-none" role="alert">
          <FontAwesomeIcon icon="info-circle" color="#a64156" />スマートフォンからのご利用は、アプリをおすすめしています。くわしくは<a href="./index.html">こちら</a>
        </div>
        <ToastContainer
          position="top-right"
          autoClose={5000}
          draggable={false}
          transition={Slide}
          newestOnTop={false}
          closeOnClick
          pauseOnFocusLoss
          bodyClassName="toastBody"
          />
        <YubinukiStorageControl slotKey={this.state.slotKey} onClickLoad={this.handleLoadYubinuki} onClickSave={this.handleSaveYubinuki} />
        <YubinukiChart
          width={600}
          height={180}
          chartScale={ChartEngine.translateToChartScaleViewModel(yubinuki)}
          itoDrawings={ChartEngine.translateToItoDrawingViewModel(yubinuki)}
          step={step}
          resolution={yubinuki.resolution} />
        <YubinukiBasicSettings 
          koma={yubinuki.koma} onChangeKoma={this.handleChangeKoma}
          tobi={yubinuki.tobi} onChangeTobi={this.handleChangeTobi}
          resolutionAuto={yubinuki.resolutionAuto}
          onChangeResolutionAuto={this.handleChangeResolutionAuto}
          resolution={yubinuki.resolution}
          onChangeResolution={this.handleChangeResolution}
          />
        <div className="procedureControl">
            <form>
                <button type="button" name="addProcedure" className="btn btn-outline-secondary mb-2" onClick={this.handleAddProcedureGroup}>
                    <FontAwesomeIcon icon="plus" />&nbsp;グループを追加する
                </button>
            </form>
        </div>
        <YubinukiProcedureGroupList
          yubinuki={yubinuki}
          groupLength={this.state.procedureGroupsLength}

          onClickAddProcedure={this.handleAddProcedure}

          onChangeOffset={this.handleChangeProcedureOffset}
          onChangeSasiType={this.handleChangeProcedureSasiType}
          onChangeMatomeSasi={this.handleChangeProcedureMatomeSasi}
          onChangeConnectedRounds={this.handleChangeProcedureConnectedRounds}

          onClickMoveUpProcedure={this.handleMoveUpProcedure}
          onClickMoveDownProcedure={this.handleMoveDownProcedure}
          onClickDeleteProcedure={this.handleDeleteProcedure}

          onChangeItoColor={this.handleChangeProcedureItoColor}
          onChangeItoRounds={this.handleChangeProcedureItoRounds}
          onClickMoveUpIto={this.handleMoveUpItoInProcedure}
          onClickMoveDownIto={this.handleMoveDownItoInProcedure}
          onClickDeleteIto={this.handleDeleteItoInProcedure}
          onClickAddIto={this.handleAddItoInProcedure}
          />
      </div>
    );
  }
}

export default App;
